import React, {ChangeEvent, useRef, useState} from 'react';

import {Icon, FullArrowLeft, Search} from '@optimaxdev/icons';
import classnames from 'classnames';

import ClearIcon from './images/delete.svg';
import s from './quickSearch.scss';

export type QuickSearchHeaderPropsType = {
    placeholder: string;
    query: string;
    onClose(): void;
    onSearchQueryChange(searchValue: string): void;
    onSearchClickHandler(
        event: React.MouseEvent<HTMLButtonElement, MouseEvent> | React.FormEvent<HTMLFormElement>,
    ): void;
};

/**
 * Component renders quick search form.
 *
 * @export
 * @param {QuickSearchHeaderPropsType} {
 *     placeholder,
 *     query,
 *     onClose,
 *     onSearchQueryChange,
 *     onSearchClickHandler,
 * }
 * @returns {React.ReactElement} - react component
 */
export function QuickSearchHeader({
    placeholder,
    query,
    onClose,
    onSearchQueryChange,
    onSearchClickHandler,
}: QuickSearchHeaderPropsType): React.ReactElement {
    const [isFocused, setIsFocused] = useState(false);
    const ref = useRef<HTMLInputElement>(null);

    const onClear = () => {
        onSearchQueryChange('');
        ref.current?.focus();
    };
    const onChange = (event: ChangeEvent<HTMLInputElement>) => {
        onSearchQueryChange(event.target.value);
    };

    return (
        <div
            className={classnames(s.header, {
                [s.headerFilled]: Boolean(query),
            })}
        >
            <button aria-label="close" type="button" className={s.arrowLeft} onClick={onClose}>
                <Icon icon={FullArrowLeft} size="responsive" />
            </button>
            <form
                className={classnames(s.inputWrap, {
                    [s.inputWrapFilled]: isFocused || Boolean(query),
                })}
                data-test-name="searchBar"
                onSubmit={onSearchClickHandler}
            >
                <button
                    type="submit"
                    className={s.searchButton}
                    data-test-name="searchSubmit"
                    aria-label="start searching for items"
                    disabled={!query}
                >
                    <Icon icon={Search} />
                </button>
                <input
                    className={s.input}
                    value={query}
                    onChange={onChange}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    type="search"
                    // eslint-disable-next-line jsx-a11y/no-autofocus
                    autoFocus
                    id="quickSearchInput"
                    placeholder={placeholder}
                    ref={ref}
                />
                <button
                    type="button"
                    aria-hidden={!query}
                    aria-label="Clear search"
                    className={classnames(s.clearButton, {
                        [s.clearButtonHidden]: !query,
                    })}
                    onClick={onClear}
                >
                    <ClearIcon />
                </button>
            </form>
        </div>
    );
}
